<script setup>
// NOTICE: KAPTION-INTEGRATION — Single-campaign delivery-events export modal
// (Path 3). Built on Dialog.vue. Lets the user pick a format (CSV / XLSX), a
// status filter and which columns to include, then exports the campaign's
// delivery events. Small exports return synchronously; large ones are processed
// in the background and polled (progress bar). Renders done / error / row-cap
// states.
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import KaptionColumnPicker from 'dashboard/components-next/Campaigns/KaptionColumnPicker.vue';
import { useCampaignExport } from 'dashboard/composables/useCampaignExport';
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';

const props = defineProps({
  // v-model:open boolean control
  open: { type: Boolean, default: false },
  campaignId: { type: [String, Number], default: '' },
  campaignTitle: { type: String, default: '' },
});

const emit = defineEmits(['update:open']);

const { t } = useI18n();
const { canUseFeature } = useFeaturePermissions();

const MAX_ROWS = 100000;

// Column definitions (order matters — matches backend ALL_COLUMNS).
const ALL_COLUMNS = [
  'event_id',
  'campaign_id',
  'campaign_title',
  'campaign_type',
  'inbox_id',
  'inbox_name',
  'contact_id',
  'contact_name',
  'phone_number',
  'template_name',
  'template_language',
  'status',
  'sent_at',
  'delivered_at',
  'read_at',
  'replied_at',
  'failed_at',
  'error_code',
  'wamid',
];

const STATUS_OPTIONS = [
  'all',
  'sent',
  'delivered',
  'read',
  'replied',
  'failed',
];

const dialogRef = ref(null);

const {
  phase,
  progress,
  downloadUrl,
  errorDetail,
  isExporting,
  startCampaignEventsExport,
  download,
  reset,
} = useCampaignExport();

const format = ref('csv'); // CSV default
const status = ref('all');
const selectedColumns = ref([...ALL_COLUMNS]); // all default-selected

// Build the {key,label} list for the picker. Labels are humanised from the key
// (the backend column names are self-descriptive enough; we Title-case them).
const columnOptions = computed(() =>
  ALL_COLUMNS.map(key => ({
    key,
    label: key
      .split('_')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' '),
  }))
);

const statusLabel = opt =>
  opt === 'all'
    ? t('CAMPAIGN.REPORT.EXPORT.ALL_STATUSES')
    : t(`CAMPAIGN.REPORT.TABLE.STATUS.${opt.toUpperCase()}`);

const isCapExceeded = computed(
  () => errorDetail.value?.code === 'ROW_LIMIT_EXCEEDED'
);

const progressInt = computed(() => Math.round(progress.value || 0));

// Sync external open prop with the underlying <dialog>.
watch(
  () => props.open,
  isOpen => {
    if (isOpen) {
      reset();
      dialogRef.value?.open();
    } else {
      dialogRef.value?.close();
    }
  }
);

const closeModal = () => {
  emit('update:open', false);
};

const handleExport = async () => {
  if (!canUseFeature('campaign_exports')) return;
  // Empty selection = all columns (per the backend contract). Send only the
  // selection when it's a strict subset.
  const columns =
    selectedColumns.value.length === ALL_COLUMNS.length
      ? []
      : selectedColumns.value;
  await startCampaignEventsExport(props.campaignId, {
    format: format.value,
    status: status.value,
    columns,
    maxRows: MAX_ROWS,
  });
  if (phase.value === 'done' && downloadUrl.value) {
    download();
  }
};

defineExpose({
  open: () => emit('update:open', true),
  close: closeModal,
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('CAMPAIGN.REPORT.EXPORT.EVENTS_TITLE')"
    :description="campaignTitle"
    width="xl"
    overflow-y-auto
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="closeModal"
  >
    <!-- Configure / processing phase -->
    <div
      v-if="
        phase === 'idle' || phase === 'submitting' || phase === 'processing'
      "
      class="flex flex-col gap-5"
    >
      <!-- Processing progress bar (large/background exports) -->
      <section v-if="phase === 'processing'" class="flex flex-col gap-2 py-4">
        <span class="text-sm text-n-slate-11">
          {{
            t('CAMPAIGN.REPORT.EXPORT.PROGRESS_LABEL', {
              progress: progressInt,
            })
          }}
        </span>
        <div class="w-full h-2 rounded-full bg-n-alpha-black4 overflow-hidden">
          <div
            class="h-full bg-n-brand transition-all duration-300"
            :style="{ width: `${progressInt}%` }"
          />
        </div>
      </section>

      <template v-else>
        <!-- FORMAT -->
        <section class="flex flex-col gap-2">
          <span
            class="text-xs font-semibold uppercase tracking-wide text-n-slate-10"
          >
            {{ t('CAMPAIGN.REPORT.EXPORT.FORMAT') }}
          </span>
          <div class="flex items-center gap-2">
            <button
              type="button"
              class="px-4 py-1.5 text-sm font-medium rounded-lg border transition-colors"
              :class="
                format === 'csv'
                  ? 'bg-n-slate-12 text-n-solid-2 border-n-slate-12'
                  : 'bg-transparent text-n-slate-11 border-n-weak hover:bg-n-alpha-black4'
              "
              @click="format = 'csv'"
            >
              {{ t('CAMPAIGN.REPORT.EXPORT.FORMAT_CSV') }}
            </button>
            <button
              type="button"
              class="px-4 py-1.5 text-sm font-medium rounded-lg border transition-colors"
              :class="
                format === 'xlsx'
                  ? 'bg-n-slate-12 text-n-solid-2 border-n-slate-12'
                  : 'bg-transparent text-n-slate-11 border-n-weak hover:bg-n-alpha-black4'
              "
              @click="format = 'xlsx'"
            >
              {{ t('CAMPAIGN.REPORT.EXPORT.FORMAT_XLSX') }}
            </button>
          </div>
        </section>

        <!-- STATUS -->
        <section class="flex flex-col gap-2">
          <span
            class="text-xs font-semibold uppercase tracking-wide text-n-slate-10"
          >
            {{ t('CAMPAIGN.REPORT.EXPORT.STATUS_LABEL') }}
          </span>
          <select
            v-model="status"
            class="w-full text-sm rounded-lg border border-n-weak bg-n-alpha-black2 px-3 py-2 text-n-slate-12 focus:outline-none focus:border-n-brand"
          >
            <option v-for="opt in STATUS_OPTIONS" :key="opt" :value="opt">
              {{ statusLabel(opt) }}
            </option>
          </select>
        </section>

        <!-- COLUMNS -->
        <section class="flex flex-col gap-2">
          <span
            class="text-xs font-semibold uppercase tracking-wide text-n-slate-10"
          >
            {{ t('CAMPAIGN.REPORT.EXPORT.COLUMNS') }}
          </span>
          <KaptionColumnPicker
            v-model="selectedColumns"
            :columns="columnOptions"
          />
        </section>

        <!-- ROWS note -->
        <p class="text-xs text-n-slate-10">
          {{ t('CAMPAIGN.REPORT.EXPORT.ROWS_CAP_DETAIL') }}
        </p>
      </template>
    </div>

    <!-- Done phase -->
    <div
      v-else-if="phase === 'done'"
      class="flex flex-col items-center justify-center gap-3 py-8"
    >
      <span class="i-lucide-check-circle w-10 h-10 text-n-teal-9" />
      <h4 class="text-base font-medium text-n-slate-12">
        {{ t('CAMPAIGN.REPORT.EXPORT.DONE_TITLE') }}
      </h4>
      <p class="text-sm text-n-slate-11 text-center">
        {{ t('CAMPAIGN.REPORT.EXPORT.DONE_MESSAGE') }}
      </p>
      <button
        type="button"
        class="text-sm font-medium text-n-blue-text hover:underline"
        @click="download"
      >
        {{ t('CAMPAIGN.REPORT.EXPORT.DOWNLOAD_AGAIN') }}
      </button>
    </div>

    <!-- Error phase -->
    <div
      v-else-if="phase === 'error'"
      class="flex flex-col items-center justify-center gap-3 py-8"
    >
      <span class="i-lucide-alert-triangle w-10 h-10 text-n-ruby-9" />
      <h4 class="text-base font-medium text-n-slate-12">
        {{ t('CAMPAIGN.REPORT.EXPORT.ERROR_TITLE') }}
      </h4>
      <p class="text-sm text-n-slate-11 text-center max-w-sm">
        {{
          isCapExceeded
            ? t('CAMPAIGN.REPORT.EXPORT.CAP_EXCEEDED_DETAIL')
            : t('CAMPAIGN.REPORT.EXPORT.ERROR_GENERIC')
        }}
      </p>
    </div>

    <template #footer>
      <!-- Configure / processing footer -->
      <div
        v-if="
          phase === 'idle' || phase === 'submitting' || phase === 'processing'
        "
        class="flex items-center justify-end w-full gap-3"
      >
        <Button
          variant="faded"
          color="slate"
          :label="t('CAMPAIGN.REPORT.EXPORT.CANCEL')"
          type="button"
          :disabled="isExporting"
          @click="closeModal"
        />
        <Button
          color="blue"
          icon="i-lucide-download"
          :label="
            isExporting
              ? t('CAMPAIGN.REPORT.EXPORT.EXPORTING')
              : t('CAMPAIGN.REPORT.EXPORT.EXPORT')
          "
          type="button"
          :is-loading="isExporting"
          :disabled="
            isExporting ||
            selectedColumns.length === 0 ||
            !canUseFeature('campaign_exports')
          "
          @click="handleExport"
        />
      </div>
      <!-- Done footer -->
      <div
        v-else-if="phase === 'done'"
        class="flex items-center justify-end w-full"
      >
        <Button
          color="slate"
          variant="faded"
          :label="t('CAMPAIGN.REPORT.EXPORT.CANCEL')"
          type="button"
          @click="closeModal"
        />
      </div>
      <!-- Error footer -->
      <div
        v-else-if="phase === 'error'"
        class="flex items-center justify-end w-full gap-3"
      >
        <Button
          variant="faded"
          color="slate"
          :label="t('CAMPAIGN.REPORT.EXPORT.CANCEL')"
          type="button"
          @click="closeModal"
        />
        <Button
          v-if="!isCapExceeded"
          color="blue"
          :label="t('CAMPAIGN.REPORT.EXPORT.TRY_AGAIN')"
          type="button"
          @click="handleExport"
        />
      </div>
    </template>
  </Dialog>
</template>
